/**
 * useCobroQuickForm
 * ------------------
 * Hook que encapsula el estado y los handlers del formulario rápido de cobro.
 *
 * Responsabilidades:
 *  - Cargar pacientes desde `/api/patients` cuando el diálogo se abre.
 *  - Autoseleccionar un paciente a partir de `prefillName` (matching tolerante).
 *  - Exponer setters atómicos (`setField`, `selectPatient`) sin filtrar
 *    detalles del estado al componente padre.
 *
 * Cada vez que se abre, el formulario empieza VACÍO salvo los datos del
 * paciente (clínica, 30/9/2026): ni fecha, ni concepto, ni método, ni nada
 * del cobro anterior (ver `buildInitialForm`).
 *
 * Phase QR2 — extraído de CobroQuickDialog para reducir su complejidad.
 */
import { useEffect, useState, useCallback } from 'react';
import { api, fullName } from './api';
import {
  findPatientMatch, patientAddressLine, buildInitialForm,
} from './cobroQuickHelpers';

export function useCobroQuickForm({
  open,
  prefillName,
  prefillPatient = null,
}) {
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState(() =>
    buildInitialForm({ patient: prefillPatient, prefillName })
  );

  // Reset form + cargar pacientes cada vez que el diálogo se abre.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Vacío desde el primer instante, sin esperar a la lista de pacientes:
    // lo escrito en la vez anterior no puede asomar mientras carga.
    setForm(buildInitialForm({ patient: prefillPatient, prefillName }));
    api.get('/api/patients').then((r) => {
      if (cancelled) return;
      const listed = Array.isArray(r.data) ? r.data : [];
      // La ficha abierta puede ser antigua y no entrar en la lista limitada
      // de pacientes recientes. Se conserva para no perder sus datos.
      const patientsWithPrefill = prefillPatient && !listed.some((p) => p.id === prefillPatient.id)
        ? [prefillPatient, ...listed]
        : listed;
      setPatients(patientsWithPrefill);
      const match = prefillPatient || findPatientMatch(patientsWithPrefill, prefillName);
      // Solo los datos del paciente: lo que la persona ya haya empezado a
      // escribir mientras cargaba la lista se respeta.
      if (match) {
        setForm((prev) => ({
          ...prev,
          patient_id: match.id || '',
          patient_name: fullName(match),
          nif: match.nif || '',
          address: patientAddressLine(match),
        }));
      }
    }).catch(() => {
      // Sin lista, el buscador sigue buscando en el servidor y el paciente
      // de la ficha ya está puesto: no hay nada que deshacer.
    });
    return () => { cancelled = true; };
  }, [open, prefillName, prefillPatient]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // `elegido` llega del buscador, que sí encuentra pacientes fuera de la lista
  // precargada. Antes, si el paciente no estaba en esa lista se BORRABA la
  // selección: al elegir un paciente antiguo el cobro se quedaba sin paciente
  // y no había forma de registrarlo.
  const selectPatient = useCallback((id, elegido) => {
    const p = elegido || patients.find((x) => x.id === id);
    if (!id) {
      setForm((prev) => ({ ...prev, patient_id: '' }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      patient_id: id,
      patient_name: p ? fullName(p) : prev.patient_name,
      nif: p ? (p.nif || '') : prev.nif,
      address: p ? patientAddressLine(p) : prev.address,
    }));
  }, [patients]);

  return { patients, form, setField, selectPatient };
}
